import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faStar, 
  faBars, 
  faTimes, 
  faUser, 
  faSignOutAlt,
  faGlobe,
  faHeart,
  faMoon,
  faMagic,
  faHandSparkles
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false);

  const navigation = [
    { name: t('home'), href: '/', icon: faHome },
    { 
      name: t('services'), 
      href: '/services', 
      icon: faStar,
      hasSubmenu: true,
      submenu: [
        { name: t('personalNumerology'), href: '/numerology', icon: faUser },
        { name: t('relationshipNumerology'), href: '/relationship-numerology', icon: faHeart },
        { name: t('astroNumerology'), href: '/astro-numerology', icon: faMoon },
        { name: t('healingNumbers'), href: '/healing-numbers', icon: faHandSparkles },
        { name: t('psychologicalNumerology'), href: '/psychological-numerology', icon: faMagic }
      ]
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setIsProfileMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = e.target.value;
    changeLanguage(newLanguage);
  };

  const navbarStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
    position: 'sticky',
    top: 0,
    zIndex: 50
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '64px'
  };

  const leftSideStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center'
  };

  const rightSideStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem'
  };

  const logoStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    textDecoration: 'none'
  };

  const logoImageStyle: React.CSSProperties = {
    height: '50px',
    width: 'auto'
  };

  const navLinkStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#374151',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'color 0.3s ease',
    position: 'relative' as const
  };

  const selectStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    outline: 'none',
    cursor: 'pointer'
  };

  const profileButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'linear-gradient(135deg, #8b5cf6, #9333ea)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    marginTop: '8px',
    width: '192px',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(168, 85, 247, 0.2)',
    padding: '8px 0',
    zIndex: 100
  };

  const servicesDropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: '8px',
    width: '240px',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(168, 85, 247, 0.2)',
    padding: '8px 0',
    zIndex: 100
  };

  const dropdownItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '8px 16px',
    color: '#374151',
    textDecoration: 'none',
    transition: 'background-color 0.3s ease',
    width: '100%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left'
  };

  const mobileMenuStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(8px)',
    borderTop: '1px solid rgba(168, 85, 247, 0.2)',
    padding: '8px 8px 12px 8px'
  };

  return (
    <nav style={navbarStyle}>
      <div style={containerStyle}>
        <div style={headerStyle}>
          {/* Left Side - Logo Only */}
          <div style={leftSideStyle}>
            <Link to="/" style={logoStyle}>
              <img 
                src="https://cdn-icons-png.flaticon.com/512/2103/2103633.png" 
                alt="Kairoscope" 
                style={logoImageStyle}
              />
              <span style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #8b5cf6, #9333ea)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.025em'
              }}>
                Kairoscope
              </span>
            </Link>
          </div>

          {/* Right Side - Navigation + Language + Profile */}
          <div style={rightSideStyle}>
            {/* Desktop Navigation */}
            <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
              {navigation.map((item) => (
                <div key={item.name} style={{ position: 'relative' }}>
                  {item.hasSubmenu ? (
                    <div
                      style={navLinkStyle}
                      onMouseEnter={() => setIsServicesMenuOpen(true)}
                      onMouseLeave={() => setIsServicesMenuOpen(false)}
                    >
                      <FontAwesomeIcon icon={item.icon} style={{ fontSize: '0.875rem' }} />
                      <span style={{ cursor: 'pointer' }}>{item.name}</span>
                      
                      {isServicesMenuOpen && (
                        <div style={servicesDropdownStyle}>
                          {item.submenu?.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              style={dropdownItemStyle}
                              onClick={() => setIsServicesMenuOpen(false)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f3ff';
                                e.currentTarget.style.color = '#7c3aed';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#374151';
                              }}
                            >
                              <FontAwesomeIcon icon={subItem.icon} style={{ color: '#7c3aed', fontSize: '0.875rem' }} />
                              <span>{subItem.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      style={navLinkStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#7c3aed';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#374151';
                      }}
                    >
                      <FontAwesomeIcon icon={item.icon} style={{ fontSize: '0.875rem' }} />
                      <span>{item.name}</span>
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Language Selector */}
            <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faGlobe} style={{ color: '#7c3aed' }} />
              <select 
                style={selectStyle}
                value={currentLanguage}
                onChange={handleLanguageChange}
              >
                {availableLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.code.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Profile/Login */}
            <div className="desktop-nav">
              {currentUser ? (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    style={profileButtonStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #7c3aed, #8b5cf6)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf6, #9333ea)';
                    }}
                  >
                    {currentUser.photoURL ? (
                      <img
                        src={currentUser.photoURL}
                        alt="Profile"
                        style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                      />
                    ) : (
                      <FontAwesomeIcon icon={faUser} />
                    )}
                    <span className="desktop-only">
                      {currentUser.displayName?.split(' ')[0]}
                    </span>
                  </button>

                  {isProfileMenuOpen && (
                    <div style={dropdownStyle}>
                      <Link
                        to="/profile"
                        style={dropdownItemStyle}
                        onClick={() => setIsProfileMenuOpen(false)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f3ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} style={{ color: '#7c3aed' }} />
                        <span>{t('profile')}</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        style={dropdownItemStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f3ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <FontAwesomeIcon icon={faSignOutAlt} style={{ color: '#ef4444' }} />
                        <span>{t('logout')}</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/auth"
                  className="mystical-button"
                  style={{ textDecoration: 'none' }}
                >
                  {t('login')}
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="mobile-nav" style={{ display: 'block' }}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  color: '#374151',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#7c3aed';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#374151';
                }}
              >
                <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} style={{ fontSize: '1.25rem' }} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="mobile-nav" style={mobileMenuStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasSubmenu ? (
                    <>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#374151',
                        padding: '8px 12px',
                        fontWeight: '600'
                      }}>
                        <FontAwesomeIcon icon={item.icon} style={{ fontSize: '0.875rem', color: '#7c3aed' }} />
                        <span>{item.name}</span>
                      </div>
                      {item.submenu?.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#6b7280',
                            textDecoration: 'none',
                            padding: '6px 12px 6px 2rem',
                            borderRadius: '6px',
                            transition: 'all 0.3s ease',
                            fontSize: '0.9rem'
                          }}
                          onClick={() => setIsMenuOpen(false)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#7c3aed';
                            e.currentTarget.style.backgroundColor = '#f5f3ff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#6b7280';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <FontAwesomeIcon icon={subItem.icon} style={{ fontSize: '0.75rem' }} />
                          <span>{subItem.name}</span>
                        </Link>
                      ))}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#374151',
                        textDecoration: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => setIsMenuOpen(false)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#7c3aed';
                        e.currentTarget.style.backgroundColor = '#f5f3ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FontAwesomeIcon icon={item.icon} style={{ fontSize: '0.875rem' }} />
                      <span>{item.name}</span>
                    </Link>
                  )}
                </div>
              ))}
              
              <div style={{ borderTop: '1px solid rgba(168, 85, 247, 0.2)', paddingTop: '12px', marginTop: '12px' }}>
                <div style={{ padding: '0 12px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faGlobe} style={{ color: '#7c3aed' }} />
                  <select 
                    style={selectStyle}
                    value={currentLanguage}
                    onChange={handleLanguageChange}
                  >
                    {availableLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {currentUser ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Link
                      to="/profile"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#374151',
                        textDecoration: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => setIsMenuOpen(false)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#7c3aed';
                        e.currentTarget.style.backgroundColor = '#f5f3ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FontAwesomeIcon icon={faUser} style={{ color: '#7c3aed' }} />
                      <span>{t('profile')}</span>
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        width: '100%',
                        textAlign: 'left',
                        color: '#374151',
                        background: 'transparent',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#dc2626';
                        e.currentTarget.style.backgroundColor = '#fef2f2';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} style={{ color: '#ef4444' }} />
                      <span>{t('logout')}</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    className="mystical-button"
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                      textDecoration: 'none'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('login')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;